import { z } from "zod";
import { EXPERIMENT_ENTITY_PREFIX, isValidPrefixedKsuid } from "@/utils/ksuidGenerator";

import {
  AssumptionDeltaSchema,
  LegacyAssumptionDeltaSchema,
  LearningConfidenceLevelSchema,
  LearningQaVerdictSchema,
  LearningTitleSchema,
} from "@/lib/toolCardSchemas/learningQa";
import {
  LegacyObservationSchema,
  ObservationSchema,
  type Observation,
} from "@/lib/toolCardSchemas/insightsDraft";

const LEARNING_ENTITY_PREFIX = "lrn_";
const INSIGHT_ENTITY_PREFIX = "ins_";

const prefixedKsuid = (prefix: string, label: string) =>
  z.string().refine((id) => isValidPrefixedKsuid(id, prefix), {
    message: `Invalid ${label} public ID`,
  });

export const LearningCardPayloadSchema = z
  .object({
    experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
    learningPublicId: prefixedKsuid(LEARNING_ENTITY_PREFIX, "learning"),
    title: LearningTitleSchema,
    narrative: z.string().min(1).max(5000),
    trackerRowCount: z.number().int().min(0),
    observations: z.array(ObservationSchema).max(20),
    approvedInsightPublicIds: z
      .array(prefixedKsuid(INSIGHT_ENTITY_PREFIX, "insight"))
      .min(1)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: "approvedInsightPublicIds must not contain duplicates",
      }),
    assumptionDelta: AssumptionDeltaSchema,
    confidenceLevel: LearningConfidenceLevelSchema,
    verdict: LearningQaVerdictSchema,
  })
  .strict();

/** Historical confirmation cards can predate projected synthesis lineage fields. */
const LegacyLearningCardPayloadSchema = z.object({
  experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
  learningPublicId: prefixedKsuid(LEARNING_ENTITY_PREFIX, "learning"),
  title: LearningTitleSchema,
  narrative: z.string().min(1),
  trackerRowCount: z.number().int().min(0).optional(),
  observations: z.array(LegacyObservationSchema).optional(),
  approvedInsightPublicIds: z
    .array(prefixedKsuid(INSIGHT_ENTITY_PREFIX, "insight"))
    .min(1),
  assumptionDelta: LegacyAssumptionDeltaSchema.optional(),
  confidenceLevel: LearningConfidenceLevelSchema,
  verdict: LearningQaVerdictSchema,
});

export const LearningCardResolveSchema = z.object({
  confirmed: z.literal(true),
}).strict();

/** The pre-migration browser echoed the canonical learning fields as its result. */
const LegacyLearningCardResultSchema = z.object({
  learningPublicId: prefixedKsuid(LEARNING_ENTITY_PREFIX, "learning"),
  title: LearningTitleSchema,
  narrative: z.string().min(1),
  approvedInsightPublicIds: z
    .array(prefixedKsuid(INSIGHT_ENTITY_PREFIX, "insight"))
    .min(1),
  assumptionDelta: LegacyAssumptionDeltaSchema.optional(),
  confidenceLevel: LearningConfidenceLevelSchema,
  verdict: LearningQaVerdictSchema,
});

export type LearningCardPayload = z.infer<typeof LearningCardPayloadSchema>;
export type LearningCardResolve = z.infer<typeof LearningCardResolveSchema>;
export type LegacyLearningCardResult = z.infer<typeof LegacyLearningCardResultSchema>;

export type LearningCardToolResult =
  | (LearningCardResolve & { toolCallId: string })
  | (LegacyLearningCardResult & { toolCallId: string });

type LearningCardEntity = {
  type?: string;
  experimentPublicId?: string;
  learningPublicId?: string;
  title?: string;
  narrative?: string;
  trackerRowCount?: number;
  observations?: Observation[];
  approvedInsightPublicIds?: string[];
  assumptionDelta?: z.infer<typeof AssumptionDeltaSchema>;
  confidenceLevel?: z.infer<typeof LearningConfidenceLevelSchema>;
  verdict?: z.infer<typeof LearningQaVerdictSchema>;
  toolInvoke?: { toolCallId?: string };
};

export type LearningCardArgs = {
  entities?: LearningCardEntity[];
  experimentPublicId?: string;
  learningPublicId?: string;
  title?: string;
  narrative?: string;
  trackerRowCount?: number;
  observations?: Observation[];
  approvedInsightPublicIds?: string[];
  assumptionDelta?: z.infer<typeof AssumptionDeltaSchema>;
  confidenceLevel?: z.infer<typeof LearningConfidenceLevelSchema>;
  verdict?: z.infer<typeof LearningQaVerdictSchema>;
};

/**
 * Parses learning_card payload from assistant tool args (flat or entities[] shape).
 */
export function parseLearningCardPayload(
  args: unknown,
  toolCallId?: string,
  allowHistoricalReceipt = false,
) {
  const direct = LearningCardPayloadSchema.safeParse(args);
  if (direct.success) {
    return direct;
  }
  if (allowHistoricalReceipt) {
    const historicalDirect = LegacyLearningCardPayloadSchema.safeParse(args);
    if (historicalDirect.success) {
      return historicalDirect;
    }
  }

  const root = args as LearningCardArgs | null | undefined;
  const entities = root?.entities ?? [];
  const entity =
    entities.find(
      (item) =>
        item.type === "learning_card" &&
        (!toolCallId || item.toolInvoke?.toolCallId === toolCallId),
    ) ?? entities.find((item) => item.type === "learning_card");

  if (!entity) {
    return direct;
  }

  const candidate = {
    experimentPublicId: entity.experimentPublicId ?? root?.experimentPublicId,
    learningPublicId: entity.learningPublicId ?? root?.learningPublicId,
    title: entity.title ?? root?.title,
    narrative: entity.narrative ?? root?.narrative,
    trackerRowCount: entity.trackerRowCount ?? root?.trackerRowCount,
    observations: entity.observations ?? root?.observations,
    approvedInsightPublicIds:
      entity.approvedInsightPublicIds ?? root?.approvedInsightPublicIds,
    assumptionDelta: entity.assumptionDelta ?? root?.assumptionDelta,
    confidenceLevel: entity.confidenceLevel ?? root?.confidenceLevel,
    verdict: entity.verdict ?? root?.verdict,
  };
  const currentEntity = LearningCardPayloadSchema.safeParse(candidate);
  if (currentEntity.success || !allowHistoricalReceipt) {
    return currentEntity;
  }
  return LegacyLearningCardPayloadSchema.safeParse(candidate);
}

/**
 * Reads historical echoed values for receipt display only. New submissions still use the strict
 * {@link LearningCardResolveSchema} confirmation contract.
 */
export function parseLegacyLearningCardResult(result: unknown) {
  return LegacyLearningCardResultSchema.safeParse(result);
}
